// src/routes/merchants/catalogue/catalogueModel.ts
import type { components } from '../../../api/generated/schema'

type Result = components['schemas']['MerchantCatalogueResult']
type Match = components['schemas']['MerchantCatalogueMatch']

/**
 * A brand from the catalogue (about 5,500 Canadian and US brands, #495): not a merchant yet. It becomes
 * one only through an explicit Add (owner decision C4). `region` is shown as it comes, a visible "CA" or
 * "US" (C1); the wire types it as a plain string, so an unexpected value is shown rather than dropped.
 */
export type Brand = {
  id: string
  name: string
  region: string
  suggestedCategory: { id: string; name: string } | null
}

/** What a pick carries beyond the merchant: the category to set with it, when the user kept "Also set category". */
export type PickExtras = { categoryId: string }

export const toBrand = (r: Result): Brand => ({
  id: r.id,
  name: r.name,
  region: r.region,
  suggestedCategory: r.suggested_category == null ? null : { id: r.suggested_category.id, name: r.suggested_category.name },
})

/** `GET /merchant-catalogue/match` answers `{brand, score}` or a bare `null`. The score is the server's business: it already applied its threshold. */
export const toBrandMatch = (m: Match | null): Brand | null => (m === null ? null : toBrand(m.brand))

/**
 * The backend's `canonical_merchant_key`, the key both its name-conflict check and its own "hide brands
 * you have" use: lowercase, unaccented, apostrophes dropped, every other run of symbols one space.
 * "Tim Horton's" → "tim hortons", "A&W" → "a w".
 */
export function brandKey(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * A string as the server's `str.strip()` leaves it: Python's whitespace, which adds the C0 separators
 * (U+001C–U+001F) and NEL (U+0085) to JavaScript's. Trimming only what JS trims would send a name the
 * server strips to nothing, a 422.
 */
export function serverTrim(s: string): string {
  let start = 0
  let end = s.length
  while (start < end && isPySpace(s.charCodeAt(start))) start++
  while (end > start && isPySpace(s.charCodeAt(end - 1))) end--
  return s.slice(start, end)
}
const isPySpace = (c: number): boolean => (c >= 0x1c && c <= 0x1f) || c === 0x85 || /\s/.test(String.fromCharCode(c))

/**
 * Brands no merchant in `names` already has. The server's search already hides the user's own and their
 * household's merchants; this also covers the shared catalogue's global merchants, which it does not
 * consider, and anything created since the search was cached. Every region of a brand goes together.
 */
export function hideOwned(brands: readonly Brand[], names: readonly string[]): Brand[] {
  const owned = new Set(names.map(brandKey))
  return brands.filter((b) => !owned.has(brandKey(b.name)))
}
